import { Home } from 'lucide-react'
import { Button } from 'shared/ui/button'

interface PageTitleProps {
  currentPage: string
  onNavigate?: (page: string) => void
  compact?: boolean
  titleOverride?: string
  descriptionOverride?: string
}

const PAGE_INFO_MAP: Record<string, { title: string; breadcrumb: string[] }> = {
  home: { title: 'หน้าแรก', breadcrumb: ['หน้าแรก'] },
  'overall-dashboard': { title: 'Overall Dashboard', breadcrumb: ['Dashboard'] },
  'plot-plan-dashboard': { title: 'Plot Plan Dashboard', breadcrumb: ['Dashboard'] },
  'permit-index': { title: 'Permit Index', breadcrumb: ['Permit', 'Permit Index'] },
  'permit-form': { title: 'สร้าง Permit ใหม่', breadcrumb: ['Permit', 'สร้าง Permit ใหม่'] },
  'permit-view': { title: 'ดู Permit', breadcrumb: ['Permit', 'ดู Permit'] },
  'permit-audit': { title: 'Permit Audit', breadcrumb: ['Permit', 'Permit Audit'] },
  timesheet: { title: 'Permit Timesheet', breadcrumb: ['Permit', 'Timesheet'] },
  'permit-delegation': { title: 'Permit Delegation', breadcrumb: ['Permit', 'Permit Delegation'] },
  'approve-permit': { title: 'Approve Permit', breadcrumb: ['Approve', 'Approve Permit'] },
  'permit-approval-detail': { title: 'รายละเอียด Permit', breadcrumb: ['Approve', 'Approve Permit', 'รายละเอียด'] },
  'open-permit': { title: 'เปิด Permit เข้าทำงาน', breadcrumb: ['Approve', 'Open Permit'] },
  'close-permit': { title: 'ปิด Permit', breadcrumb: ['Approve', 'Close Permit'] },
  report: { title: 'Report', breadcrumb: ['Report'] },
  'create-user-account': { title: 'Create User Account', breadcrumb: ['Admin', 'Create User Account'] },
  'contractor-management': { title: 'Contractor Management', breadcrumb: ['Admin', 'Contractor Management'] },
  'area-master': { title: 'Area Master', breadcrumb: ['Admin', 'Area Master'] },
  'company-master': { title: 'Company Master', breadcrumb: ['Admin', 'Company Master'] },
  'assign-authorize': { title: 'Assign Authorize', breadcrumb: ['Admin', 'Assign Authorize'] },
  'role-user-mapping': { title: 'User-Role Mapping', breadcrumb: ['Admin', 'User-Role Mapping'] },
  'system-configuration': { title: 'System Configuration', breadcrumb: ['Admin', 'System Configuration'] },
  'site-configuration': { title: 'Site Configuration', breadcrumb: ['Admin', 'Site Configuration'] },
  'workflow-configuration': { title: 'Workflow Configuration', breadcrumb: ['Admin', 'Workflow Configuration'] },
  'dynamic-form-configuration': { title: 'Dynamic Form Configuration', breadcrumb: ['Admin', 'Dynamic Form Configuration'] },
  'dynamic-form-configuration-editor': { title: 'Dynamic Form Template Editor', breadcrumb: ['Admin', 'Dynamic Form Template Editor'] },
  'dynamic-report-configuration': { title: 'Dynamic Report Configuration', breadcrumb: ['Admin', 'Dynamic Report Configuration'] },
  'dynamic-report-configuration-editor': { title: 'Dynamic Report Template Editor', breadcrumb: ['Admin', 'Dynamic Report Template Editor'] },
  'dynamic-report-run-preview': { title: 'Dynamic Report Run Preview', breadcrumb: ['Admin', 'Dynamic Report Run Preview'] },
  'email-configuration': { title: 'Email Configuration', breadcrumb: ['Admin', 'Email Configuration'] },
  'email-template-configuration': { title: 'Email Template Configuration', breadcrumb: ['Admin', 'Email Template Configuration'] },
  'permit-delegation-configuration': { title: 'Permit Delegation Configuration', breadcrumb: ['Admin', 'Permit Delegation Configuration'] },
  'active-directory-configuration': { title: 'Active Directory Configuration', breadcrumb: ['Admin', 'Active Directory Configuration'] },
  'azure-ad-configuration': { title: 'Azure AD Configuration', breadcrumb: ['Admin', 'Azure AD Configuration'] },
  'company-management': { title: 'Company Management', breadcrumb: ['Admin', 'Company Management'] },
  contact: { title: 'Contact', breadcrumb: ['Contact'] },
}

const PAGE_DESCRIPTION_MAP: Record<string, string> = {
  'permit-form': 'กรอกข้อมูลเพื่อสร้างเอกสารขออนุญาตทำงานใหม่',
  'permit-index': 'จัดการและติดตามสถานะเอกสาร Permit to Work ทั้งหมด',
  'overall-dashboard': 'แสดงภาพรวมเอกสาร Permit พร้อม Layout พื้นที่ปฏิบัติงานแบบ Plot Plan หรือ Map ตามเงื่อนไขที่ค้นหา',
  'plot-plan-dashboard': 'แสดงตำแหน่งการทำงานในแต่ละพื้นที่ตามประเภท Permit พร้อมรายละเอียดการปฏิบัติงาน',
  'approve-permit': 'ตรวจสอบและอนุมัติเอกสาร Permit to Work ที่รอการอนุมัติ',
  'open-permit': 'รายการ Permit ที่ผ่านการอนุมัติและพร้อมเปิดเข้าทำงาน',
  'close-permit': 'รายการ Permit ที่เปิดทำงานแล้วและพร้อมปิด พร้อมการตรวจสอบการเคลียร์พื้นที่',
  'permit-audit': 'ตรวจสอบและประเมินคุณภาพของ Permit ที่ได้รับการอนุมัติแล้ว เพื่อให้มั่นใจในมาตรฐานการปฏิบัติงาน',
  'permit-delegation': 'จัดการรายการมอบหมายสิทธิ์อนุมัติแทนในช่วงที่ไม่อยู่',
  'create-user-account': 'จัดการบัญชีผู้ใช้งานระบบ E-PTW รวมถึงการกำหนดสิทธิ์และบทบาท',
  'contractor-management': 'จัดการข้อมูลผู้รับเหมาและบุคคลภายนอก รวมถึงการจัดการสัญญา ใบอนุญาต และข้อมูลความปลอดภัย',
  'area-master': 'จัดการข้อมูลพื้นที่และโซนการทำงาน รวมถึงการกำหนดความเสี่ยงและประเภท Permit ที่อนุญาต',
  'company-master': 'จัดการข้อมูลบริษัทและผู้รับเหมา รวมถึงการจัดการสัญญา มาตรฐาน และใบรับรอง',
  'assign-authorize': 'จัดการสิทธิ์การใช้งานและการอนุมัติของผู้ใช้ในระบบ รวมถึงการกำหนดพื้นที่และประเภทงานที่สามารถเข้าถึงได้',
  'system-configuration': 'กำหนดชื่อระบบ โลโก้ favicon และข้อมูลพื้นฐานสำหรับการแสดงผลของแพลตฟอร์ม',
  'site-configuration': 'จัดการการตั้งค่าและการกำหนดค่า Site ต่างๆ ในระบบ รวมถึงฟีเจอร์และการตั้งค่าเฉพาะแต่ละ Site',
  'workflow-configuration': 'กำหนดขั้นตอนการอนุมัติ Workflow สำหรับแต่ละ Site และประเภท Permit รวมถึงเงื่อนไขและกฎการอนุมัติ',
  'email-configuration': 'Configure SMTP and base URL per environment for system emails',
  'email-template-configuration': 'Create and maintain tenant-specific email templates for approval, revise, and notification flows',
  'permit-delegation-configuration': 'จัดการ delegation ของผู้ใช้งานสำหรับแต่ละ tenant ผ่านหน้า Admin',
  'dynamic-form-configuration': 'กำหนด Input Fields ที่จะแสดงในฟอร์มตามประเภท Permit ให้ลูกค้าสามารถปรับแต่งได้เอง',
  'dynamic-form-configuration-editor': 'Edit selected template structure and section visibility',
  'dynamic-report-configuration': 'Create and maintain dynamic report templates, columns, and data source settings',
  'dynamic-report-configuration-editor': 'Configure fields and filter presets for selected report template',
  'dynamic-report-run-preview': 'Run selected report template with runtime filters and preview result',
  'active-directory-configuration': 'การตั้งค่าการเชื่อมต่อกับ Active Directory สำหรับ User Authentication และ Role Mapping',
  'azure-ad-configuration': 'การตั้งค่าการเชื่อมต่อ Azure AD สำหรับการยืนยันตัวตนผู้ใช้งาน',
}

export function PageTitle({
  currentPage,
  onNavigate,
  compact = false,
  titleOverride,
  descriptionOverride,
}: PageTitleProps) {
  const pageInfo = PAGE_INFO_MAP[currentPage] ?? PAGE_INFO_MAP.home
  const description = descriptionOverride ?? PAGE_DESCRIPTION_MAP[currentPage] ?? ''
  const title = titleOverride ?? pageInfo.title

  const isAdminPage = pageInfo.breadcrumb[0] === 'Admin'
  const containerClasses = compact ? 'pb-2 mb-3' : 'pb-4 mb-6'

  return (
    <div className={containerClasses}>
      {isAdminPage && onNavigate ? (
        <div className="mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('admin-menu')}
            className="p-0 h-auto text-sm text-indigo-600 hover:text-indigo-700"
          >
            <Home className="w-4 h-4 mr-2" />
            กลับหน้าหลัก Admin
          </Button>
        </div>
      ) : null}

      <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
      {description && !isAdminPage ? <p className="text-gray-600 mt-1">{description}</p> : null}
    </div>
  )
}
